import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { RouterLink } from '@angular/router';
import { EditTeamMemberComponent } from '../../edit-team/edit-team-member.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AvatarComponent, ButtonComponent, LoaderModule, TagComponent } from '@mds/ui';
import { TeamSettingSection } from '@mds/common';
import { InviteTeamComponent } from '../../invite-team/invite-team.component';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { NO_ACTIVITIES } from '@pages/profile/utils/constant';
import { NoDataComponent } from '../../../../no-data/no-data.component';
import { BehaviorSubject } from 'rxjs';
import { GetInitialsPipe } from '@pipes/get-initials.pipe';

@Component({
  selector: 'app-member-settings',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    LoaderModule,
    ButtonComponent,
    InviteTeamComponent,
    AvatarComponent,
    MdsIconsModule,
    MatDialogModule,
    NoDataComponent,
    TagComponent,
    GetInitialsPipe
  ],
  templateUrl: './member-settings.component.html',
  styleUrls: ['../../team-common.scss', './member-settings.component.scss']
})
export class MemberSettingsComponent implements OnInit, OnChanges {
  @Input() isEditable: boolean;
  @Input() teamName!: string;
  @Input() teamRid!: string;
  @Input() userRid!: string;
  @Input() section!: TeamSettingSection;
  @Output() reload = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<void>();
  icons = MdsIcon;
  teamMembers = [];
  loading = true;
  invitations = [
    {
      inviteEmail: '',
      inviteRid: '',
      fieldName: 'input_0'
    }
  ];
  reset = new BehaviorSubject({});
  emptyData = {
    iconDetails: {
      name: MdsIcon.EMPTY_ACTIVITIES,
      width: 36.5,
      height: 44
    },
    title: 'No members yet',
    subtitle: 'Invite new members to see it here'
  };
  protected readonly emptyAcivities = NO_ACTIVITIES;
  private makersiteService = inject(MakersiteService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    if (this.section) {
      this.getMembers();
    }
  }

  ngOnChanges(): void {
    if (this.section) {
      this.getMembers();
    }
  }

  inviteCoWorker() {
    const invitation: Record<string, any> = {
      to_email: [],
      to_user: [],
      shared_resource: `${this.teamName}.makersite.app`,
      shared_resource_rid: this.teamRid,
      message: `<p> We would like you to join our team <b>${this.teamName}</b> on makersite </p>`
    };
    invitation.rid = this.teamRid;
    this.invitations.forEach((el) => {
      if (el.inviteRid) {
        invitation.to_user.push(el.inviteRid);
      } else if (el.inviteEmail) {
        invitation.to_email.push(el.inviteEmail);
      }
    });
    this.makersiteService.sendInvition(invitation, invitation.rid).subscribe(
      () => {
        this.toastr.showMessage('User added to team successfully', 1000);
        this.invitations = [
          {
            inviteEmail: '',
            inviteRid: '',
            fieldName: 'input_0'
          }
        ];
        this.reload.emit(true);
        this.reset.next({});
      },
      () => {}
    );
  }

  closeDialog() {
    this.close.emit();
  }

  editUsersData(user: any) {
    const dialogRef = this.dialog.open(EditTeamMemberComponent, {
      panelClass: 'edit-team-member',
      data: {
        formSchema: this.section.formSchema,
        lines: user.lines,
        teamRid: this.teamRid
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.reload.emit(true);
      }
    });
  }

  private getMembers() {
    this.teamMembers = [];
    this.loading = true;
    let info: Record<string, any> = {};
    this.section?.lines.forEach((e) => {
      info = this.getMemberInfo(e);
      info.lines = e;

      const exists = this.teamMembers.some((user) => user.rid === info.rid);
      if (!exists) {
        this.teamMembers.push(info);
      }
    });
    this.loading = false;
  }

  private getMemberInfo(data) {
    const membersData: Record<string, any> = {};
    data.forEach((e) => {
      if (e.key.endsWith('.in')) {
        Object.assign(membersData, e);
      } else if (e.key.endsWith('.rid')) {
        membersData.isUserTeamManager = e.label === 'Manager';
      } else {
        const key = e.key.split('.').pop()!;
        membersData[key] = e.value;
      }
    });
    return membersData;
  }
}
